/**
 * ShipmentKanbanCard - Card do Kanban de Remessas (somente leitura).
 *
 * Reaproveita as peças oficiais da listagem (ShipmentDeadlineChip,
 * ShipmentTrackingCode, recipientKindLabel) para não divergir da tabela.
 *
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial.
 */
import { format } from 'date-fns';
import { CalendarDays, Package, Truck } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ShipmentRow } from '@/hooks/useShipments';
import { getShipmentDeadline } from '@/lib/shipmentDeadline';
import { recipientKindLabel } from '@/lib/shipmentLinkPermissions';
import { ShipmentDeadlineChip } from '../ShipmentDeadlineChip';
import { ShipmentTrackingCode } from '../ShipmentTrackingCode';

const brl = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDate = (d: string) => format(new Date(d.length === 10 ? `${d}T12:00:00` : d), 'dd/MM/yyyy');

export function ShipmentKanbanCard({ shipment: r, onOpen, now }: { shipment: ShipmentRow; onOpen: (id: string) => void; now: Date }) {
  const deadline = getShipmentDeadline(r.expected_delivery_date, r.delivered_at, r.status, now);
  return (
    <div role="button" tabIndex={0} onClick={() => onOpen(r.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(r.id); } }}
      className="cursor-pointer space-y-2 rounded-lg bg-card p-3 shadow-sm transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {/* Número + tipo */}
      <div className="flex items-center gap-1.5">
        <span className="whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium tabular-nums">{r.number}</span>
        {r.direction === 'return' && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">Retorno</span>}
        {Number(r.freight_cost) > 0 && <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">{brl(Number(r.freight_cost))}</span>}
      </div>
      {/* Destinatário */}
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{r.recipient_name}</p>
        <p className="truncate text-[11px] text-muted-foreground">{recipientKindLabel(r.recipient_customer_id, r.recipient_roles)}</p>
      </div>
      {/* Transportadora + rastreio */}
      <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
        <Truck className="h-3 w-3 shrink-0" />
        <span className="truncate">{r.carrier_name ?? 'Sem transportadora'}</span>
      </div>
      {r.tracking_code && <ShipmentTrackingCode code={r.tracking_code} />}
      {deadline && <ShipmentDeadlineChip variant="compact" deadline={deadline} />}
      {/* Rodapé */}
      <div className="flex items-center gap-3 border-t pt-2 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1"><Package className="h-3 w-3" />{r.items_count ?? 0} item(ns)</span>
        {r.expected_delivery_date && (
          <span className={cn('ml-auto flex items-center gap-1', r.is_overdue && 'text-destructive')}>
            <CalendarDays className="h-3 w-3" />{fmtDate(r.expected_delivery_date)}
          </span>
        )}
      </div>
    </div>
  );
}
